import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import { MapPin, Clock, Briefcase, Search, Loader2, Sparkles } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_LABELS = { full_time: 'Puno radno vrijeme', part_time: 'Nepuno', contract: 'Ugovor', internship: 'Praksa', remote: 'Remote' };
const CAT_LABELS = { it: 'IT', admin: 'Administracija', proizvodnja: 'Proizvodnja', trgovina: 'Trgovina', zdravstvo: 'Zdravstvo', obrazovanje: 'Obrazovanje', ugostiteljstvo: 'Ugostiteljstvo', promet: 'Promet', ostalo: 'Ostalo' };

export default function JobsPage() {
  const { t } = useTranslation();
  const [jobs, setJobs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [q, setQ] = useState('');
  const [category, setCategory] = useState('');
  const [loading, setLoading] = useState(true);

  const loadJobs = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams({ page, limit: 12 });
    if (category) params.set('category', category);
    if (q) params.set('q', q);
    try {
      const r = await fetch(`${API}/public/jobs?${params}`);
      if (r.ok) { const d = await r.json(); setJobs(d.jobs || []); setTotal(d.total || 0); }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [page, category, q]);

  useEffect(() => { loadJobs(); }, [loadJobs]);

  return (
    <>
      <Helmet><title>{`Posao \u2014 e-mina.eu`}</title></Helmet>
      <div className="max-w-7xl mx-auto px-4 py-8" data-testid="jobs-page">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold">E-MINA Posao</h1>
          <Link to="/posao/predaj" className="px-4 py-2 bg-red-600 text-white text-sm rounded-lg font-medium hover:bg-red-700" data-testid="submit-job-link">Predaj oglas</Link>
        </div>
        <div className="flex gap-3 mb-6 flex-wrap">
          <div className="flex items-center gap-2 flex-1 min-w-[200px] bg-white dark:bg-[#111827] border border-gray-200 dark:border-gray-800 rounded-lg px-3">
            <Search size={16} className="text-gray-400" />
            <input type="text" value={q} onChange={e => setQ(e.target.value)} onKeyDown={e => e.key === 'Enter' && loadJobs()} placeholder="Pretraži poslove..." className="py-2.5 text-sm w-full outline-none" data-testid="job-search" />
          </div>
          <select value={category} onChange={e => { setCategory(e.target.value); setPage(1); }} className="bg-white dark:bg-[#111827] border border-gray-200 dark:border-gray-800 rounded-lg px-3 py-2.5 text-sm" data-testid="job-category-filter">
            <option value="">Sve kategorije</option>
            {Object.entries(CAT_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </div>
        {loading ? <div className="flex justify-center py-12"><Loader2 className="animate-spin text-gray-400" /></div> :
        jobs.length === 0 ? <div className="text-center py-12 text-gray-400">Nema oglasa za posao</div> : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {jobs.map(j => {
              const promoted = j.promoted_until && new Date(j.promoted_until) > new Date();
              return (
              <Link key={j.id} to={`/posao/${j.id}`}
                className={`group bg-white dark:bg-[#111827] rounded-xl p-5 hover:shadow-md transition-shadow ${promoted ? 'border-2 border-amber-400 ring-1 ring-amber-300/40 relative' : 'border border-gray-200 dark:border-gray-800'}`}
                data-testid={`job-card-${j.id}`}
                data-promoted={promoted ? '1' : '0'}>
                {promoted && (
                  <span className="absolute top-2 right-2 inline-flex items-center gap-1 text-[10px] font-bold uppercase text-amber-900 bg-amber-300 px-2 py-0.5 rounded-full shadow" data-testid="promoted-job-badge">
                    <Sparkles size={10} /> Istaknuto
                  </span>
                )}
                <div className="flex items-start gap-3 mb-3">
                  <div className="w-10 h-10 bg-gray-100 dark:bg-[#0f172a] rounded-lg flex items-center justify-center shrink-0 overflow-hidden">
                    {j.company_logo_url ? (
                      <img src={j.company_logo_url} alt={j.company} className="w-full h-full object-contain" data-testid={`job-card-logo-${j.id}`} />
                    ) : (
                      <Briefcase size={18} className="text-gray-400" />
                    )}
                  </div>
                  <div><h3 className="font-semibold text-gray-900 dark:text-gray-100 group-hover:text-red-600 transition-colors text-sm">{j.title}</h3><p className="text-xs text-gray-500">{j.company}</p></div>
                </div>
                <div className="flex flex-wrap gap-2 text-xs text-gray-500">
                  <span className="flex items-center gap-1"><MapPin size={12} />{j.location}</span>
                  <span className="px-2 py-0.5 bg-blue-50 text-blue-600 rounded-full">{TYPE_LABELS[j.employment_type] || j.employment_type}</span>
                  {j.salary_range_eur && <span className="font-medium text-emerald-600">{j.salary_range_eur} €</span>}
                </div>
              </Link>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
}
